import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

export const COLORS = ['#f4931d', '#178c98', '#2d3748', '#e53e3e', '#38a169', '#805ad5', '#d69e2e', '#3182ce'];

export const fmt = (n) => `$${(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function LoadingState() {
  return <div className="flex justify-center py-16"><Loader2 size={32} className="animate-spin text-[#f4931d]" /></div>;
}

export function StatCard({ label, value, sub, color = 'bg-[#f4931d]' }) {
  return (
    <div className="bg-white rounded-[10px] border p-5">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className={`text-xs mt-1 ${color === 'text-green-600' ? 'text-green-600' : color === 'text-red-600' ? 'text-red-600' : 'text-slate-400'}`}>{sub}</div>}
    </div>
  );
}

export function DonutChart({ data, title }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-[10px] border p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
            {data.map((entry, i) => <Cell key={entry.name || i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BarGraph({ data, title, dataKey = 'value', nameKey = 'name' }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-[10px] border p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey={dataKey} fill="#f4931d" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReportTable({ headers, rows, footer, testId }) {
  return (
    <div className="bg-white rounded-[10px] border overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead className="bg-slate-50 border-b">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-4 py-12 text-center text-slate-400">No data found for this period</td></tr>
          ) : rows.map((row, ri) => {
            // Reports are static / regenerated wholesale per fetch — derive a
            // content-stable key from the first cell when available.
            const rowKey = (Array.isArray(row) && row[0] != null && String(row[0]).length)
              ? `${ri}-${String(row[0])}`
              : `r-${ri}`;
            return (
              <tr key={rowKey} className="hover:bg-orange-50/30 transition-colors">
                {row.map((cell, ci) => (
                  <td key={`${rowKey}-${ci}`} className="px-4 py-2.5 whitespace-nowrap">{cell}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {footer && rows.length > 0 && (
          <tfoot>
            <tr className="bg-slate-800 text-white font-semibold">
              {footer.map((cell, i) => (
                <td key={`footer-${i}`} className="px-4 py-3 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export function StatusPipeline({ breakdown, title }) {
  if (!breakdown) return null;
  const funnelData = [
    { stage: 'Draft', count: breakdown.Draft || 0 },
    { stage: 'Assigned', count: breakdown.Assigned || 0 },
    { stage: 'For Submission', count: breakdown['For Submission'] || 0 },
    { stage: 'For Acceptance', count: breakdown['For Acceptance'] || 0 },
    { stage: 'Accepted', count: breakdown.Accepted || 0 },
    { stage: 'Job Booked', count: breakdown['Job Booked'] || 0 },
    { stage: 'Invoiced', count: breakdown.Invoiced || 0 },
  ].filter(d => d.count > 0);
  if (funnelData.length === 0) return null;
  const maxCount = Math.max(...funnelData.map(f => f.count), 1);
  return (
    <div className="bg-white rounded-[10px] border p-4">
      <h5 className="text-xs font-semibold text-slate-600 mb-3">{title}</h5>
      <div className="space-y-1.5">
        {funnelData.map(d => {
          const width = Math.max((d.count / maxCount) * 100, 8);
          const stageColor = d.stage === 'Declined' ? 'bg-red-400' : d.stage === 'Invoiced' ? 'bg-green-500' : d.stage === 'Accepted' || d.stage === 'Job Booked' ? 'bg-green-400' : 'bg-[#f4931d]';
          return (
            <div key={d.stage} className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500 w-24 text-right shrink-0">{d.stage}</span>
              <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                <div className={`h-full ${stageColor} rounded flex items-center justify-end pr-1.5 transition-all`} style={{ width: `${width}%` }}>
                  <span className="text-[10px] font-bold text-white">{d.count}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ConversionBadge({ rate }) {
  let color = 'bg-red-100 text-red-700';
  if (rate >= 70) color = 'bg-green-100 text-green-700';
  else if (rate >= 40) color = 'bg-yellow-100 text-yellow-700';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{rate}%</span>;
}

export function QuoteStatusPill({ status }) {
  const colors = {
    'Draft': 'bg-slate-100 text-slate-600',
    'Assigned': 'bg-blue-100 text-blue-700',
    'For Submission': 'bg-purple-100 text-purple-700',
    'For Acceptance': 'bg-yellow-100 text-yellow-700',
    'Accepted': 'bg-green-100 text-green-700',
    'Declined': 'bg-red-100 text-red-700',
    'Job Booked': 'bg-teal-100 text-teal-700',
    'Invoiced': 'bg-orange-100 text-orange-700',
    'Cancelled': 'bg-gray-100 text-gray-600',
  };
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
}
